/**
 * ExcelTable - Grouped table with agg system subtotals
 */

import React, {
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Table,
  TableBody,
  TablePagination,
  Paper,
  Typography,
} from "@material-ui/core";
import { useColumnFilters, usePagination } from "../../../hooks";
import { buildExcelGroups } from "../../../builders";
import {
  getComparator,
  calcRemainingGal,
  calcRemainingAf,
  computePctPermit,
  formatGallons,
  formatPermit,
  formatAcreFeet,
} from "../../../utils";
import { COLUMN_MODES } from "../../../constants";
import { exportExcelTable } from "../../../exports";
import { PercentBar, StatusCell } from "../../atoms";
import {
  SummaryTableContainer,
  StyledTableCell,
  StickyFirstCell,
  StickySecondCell,
  YearColoredGroupHeader,
  YearColoredHeaderStickyCell,
  YearColoredHeaderSecondStickyCell,
  YearColoredWellRow,
  YearColoredStickyCell,
  YearColoredSecondStickyCell,
  TotalsRow,
  NoDataMessage,
} from "../../styled";
import ExcelHeader from "./ExcelHeader";

/**
 * Excel-style table component
 * @param {Object} props
 * @param {Array} props.rows - Data rows
 * @param {string} props.columnMode - Column display mode
 * @param {React.Ref} ref - Ref for imperative handle
 */
const ExcelTable = forwardRef(({ rows, columnMode }, ref) => {
  const {
    columnFilters,
    filterOptions,
    orderBy,
    order,
    handleFilterChange,
    handleSort,
    filteredAndSortedRows,
  } = useColumnFilters(rows, "cuwcd_well_number", "asc");

  const { groups, grandTotals, years } = useMemo(
    () => buildExcelGroups(filteredAndSortedRows),
    [filteredAndSortedRows]
  );

  // Create year-to-index mapping for colors
  const yearColorIndex = useMemo(() => {
    const map = new Map();
    years.forEach((year, index) => map.set(year, index));
    return map;
  }, [years]);

  // Sort wells within each group
  const sortedGroups = useMemo(() => {
    return groups.map((group) => ({
      ...group,
      wells: [...group.wells].sort(getComparator(order, orderBy)),
    }));
  }, [groups, order, orderBy]);

  const {
    page,
    perPage,
    handleChangePage,
    handleChangePerPage,
    paginatedItems: paginatedGroups,
  } = usePagination(sortedGroups, 10);

  const grandRemainingGal = calcRemainingGal(grandTotals);
  const grandRemainingAf = calcRemainingAf(grandTotals);
  const isGrandNegative = grandRemainingGal < 0;

  const handleExport = useCallback(() => {
    exportExcelTable(sortedGroups, grandTotals, columnMode);
  }, [sortedGroups, grandTotals, columnMode]);

  // Expose export function and counts to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      exportCsv: handleExport,
      groupCount: sortedGroups.length,
      rowCount: filteredAndSortedRows.length,
    }),
    [handleExport, sortedGroups.length, filteredAndSortedRows.length]
  );

  if (!rows || rows.length === 0) {
    return (
      <NoDataMessage>
        <Typography variant="body1">No data for selected filters.</Typography>
      </NoDataMessage>
    );
  }

  return (
    <>
      <SummaryTableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <ExcelHeader
            columnMode={columnMode}
            orderBy={orderBy}
            order={order}
            onSort={handleSort}
            columnFilters={columnFilters}
            filterOptions={filterOptions}
            onFilterChange={handleFilterChange}
          />
          <TableBody>
            {paginatedGroups.map((group) => {
              const groupRemainingGal = calcRemainingGal(group.totals);
              const groupRemainingAf = calcRemainingAf(group.totals);
              const isGroupNegative = groupRemainingGal < 0;
              const yearIdx = yearColorIndex.get(group.report_year) || 0;

              return (
                <React.Fragment
                  key={`${group.agg_system_ndx ?? "null"}-${group.report_year}`}
                >
                  {/* Agg System + Year Group Header */}
                  <YearColoredGroupHeader $yearIndex={yearIdx}>
                    <YearColoredHeaderStickyCell $yearIndex={yearIdx}>
                      {group.report_year}
                    </YearColoredHeaderStickyCell>
                    <YearColoredHeaderSecondStickyCell $yearIndex={yearIdx}>
                      {group.agg_system_name}
                    </YearColoredHeaderSecondStickyCell>
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell align="right">
                      {formatPermit(group.totals.h_permit_af)}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {formatPermit(group.totals.o_permit_af)}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {formatPermit(group.totals.total_permit_af)}
                    </StyledTableCell>
                    {columnMode === COLUMN_MODES.full && (
                      <>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.jan)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.feb)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.mar)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.apr)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.may)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.jun)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.jul)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.aug)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.sep)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.oct)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.nov)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatGallons(group.totals.dec)}
                        </StyledTableCell>
                      </>
                    )}
                    <StyledTableCell align="right">
                      {formatGallons(group.totals.ytd_gal)}
                    </StyledTableCell>
                    <StatusCell negative={isGroupNegative}>
                      {formatGallons(groupRemainingGal)}
                    </StatusCell>
                    <StyledTableCell align="right">
                      {formatAcreFeet(group.totals.ytd_af)}
                    </StyledTableCell>
                    <StatusCell negative={isGroupNegative}>
                      {formatAcreFeet(groupRemainingAf)}
                    </StatusCell>
                    <PercentBar value={group.pct_permit} />
                  </YearColoredGroupHeader>

                  {/* Individual Wells */}
                  {group.wells.map((well) => {
                    const wellRemainingGal = calcRemainingGal(well);
                    const wellRemainingAf = calcRemainingAf(well);
                    const isWellNegative = wellRemainingGal < 0;
                    const wellPctPermit = computePctPermit(well);

                    return (
                      <YearColoredWellRow
                        key={`${well.well_ndx}-${well.report_year}`}
                        $yearIndex={yearIdx}
                      >
                        <YearColoredStickyCell $yearIndex={yearIdx}>
                          {well.cuwcd_well_number}
                        </YearColoredStickyCell>
                        <YearColoredSecondStickyCell $yearIndex={yearIdx} />
                        <StyledTableCell>
                          {well.state_well_number}
                        </StyledTableCell>
                        <StyledTableCell>{well.well_name}</StyledTableCell>
                        <StyledTableCell>{well.report_year}</StyledTableCell>
                        <StyledTableCell>{well.aquifer_name}</StyledTableCell>
                        <StyledTableCell align="right">
                          {formatPermit(well.h_permit_af)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatPermit(well.o_permit_af)}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {formatPermit(well.total_permit_af)}
                        </StyledTableCell>
                        {columnMode === COLUMN_MODES.full && (
                          <>
                            <StyledTableCell align="right">
                              {formatGallons(well.jan)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.feb)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.mar)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.apr)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.may)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.jun)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.jul)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.aug)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.sep)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.oct)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.nov)}
                            </StyledTableCell>
                            <StyledTableCell align="right">
                              {formatGallons(well.dec)}
                            </StyledTableCell>
                          </>
                        )}
                        <StyledTableCell align="right">
                          {formatGallons(well.ytd_gal)}
                        </StyledTableCell>
                        <StatusCell negative={isWellNegative}>
                          {formatGallons(wellRemainingGal)}
                        </StatusCell>
                        <StyledTableCell align="right">
                          {formatAcreFeet(well.ytd_af)}
                        </StyledTableCell>
                        <StatusCell negative={isWellNegative}>
                          {formatAcreFeet(wellRemainingAf)}
                        </StatusCell>
                        <PercentBar value={wellPctPermit} />
                      </YearColoredWellRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
            <TotalsRow>
              <StickyFirstCell>
                <strong>Grand Total</strong>
              </StickyFirstCell>
              <StickySecondCell></StickySecondCell>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell align="right">
                {formatPermit(grandTotals.h_permit_af)}
              </StyledTableCell>
              <StyledTableCell align="right">
                {formatPermit(grandTotals.o_permit_af)}
              </StyledTableCell>
              <StyledTableCell align="right">
                {formatPermit(grandTotals.total_permit_af)}
              </StyledTableCell>
              {columnMode === COLUMN_MODES.full && (
                <>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.jan)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.feb)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.mar)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.apr)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.may)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.jun)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.jul)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.aug)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.sep)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.oct)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.nov)}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {formatGallons(grandTotals.dec)}
                  </StyledTableCell>
                </>
              )}
              <StyledTableCell align="right">
                {formatGallons(grandTotals.ytd_gal)}
              </StyledTableCell>
              <StatusCell negative={isGrandNegative}>
                {formatGallons(grandRemainingGal)}
              </StatusCell>
              <StyledTableCell align="right">
                {formatAcreFeet(grandTotals.ytd_af)}
              </StyledTableCell>
              <StatusCell negative={isGrandNegative}>
                {formatAcreFeet(grandRemainingAf)}
              </StatusCell>
              <PercentBar value={grandTotals.pct_permit} />
            </TotalsRow>
          </TableBody>
        </Table>
      </SummaryTableContainer>
      <TablePagination
        component="div"
        count={sortedGroups.length}
        page={page}
        onChangePage={handleChangePage}
        rowsPerPage={perPage}
        onChangeRowsPerPage={handleChangePerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Groups per page:"
      />
    </>
  );
});

export default ExcelTable;
